import React from "react";
import { motion } from "framer-motion";
import {
  PhoneCall,
  CalendarCheck2,
  BrainCircuit,
  RefreshCw,
  LineChart,
} from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
      when: "beforeChildren",
      staggerChildren: 0.12,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const pulseRing = {
  initial: { opacity: 0.2, scale: 1 },
  animate: {
    opacity: [0.2, 0, 0.2],
    scale: [1, 1.25, 1],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

export function RevenueImpactSection() {
  const points = [
    {
      icon: PhoneCall,
      label: "100% Lead Capture",
      body: "Every inbound call is answered, logged, and tracked instantly. Stop letting potential revenue die in your voicemail.",
    },
    {
      icon: CalendarCheck2,
      label: "Real-Time Conversion",
      body: "Calyro locks in the booking while the customer is hot on the line—eliminating the game of phone tag.",
    },
    {
      icon: BrainCircuit,
      label: "Smart Intent Triage",
      body: "The system detects if a caller is a \"New Patient,\" \"Emergency Repair,\" or just \"Spam,\" and routes them accordingly.",
    },
    {
      icon: RefreshCw,
      label: "The \"Safety Net\" Follow-Up",
      body: "Caller hung up? Calyro automatically triggers an SMS follow-up to re-engage them before they call a competitor.",
    },
    {
      icon: LineChart,
      label: "True ROI Tracking",
      body: "Finally see which marketing campaigns are driving the calls that actually turn into cash.",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-white py-16 sm:py-20">
      {/* Glow background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-purple-500/10 blur-3xl" />
      </div>
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.05)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      <div className="relative mx-auto flex max-w-6xl flex-col gap-12 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
        {/* Left: Headline & narrative */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="w-full max-w-xl space-y-6 text-left"
        >
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.16em] text-blue-600">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
            Revenue Engine
          </motion.div>

          <motion.h2
            variants={itemVariants}
            className="text-balance text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl leading-[1.1]"
          >
            Turn Your Front Desk Into a{" "}
            <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-purple-600 bg-clip-text text-transparent">
              Profit Center.
            </span>
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Calyro transforms your phone line from a blind spot into a growth engine. It captures every lead, qualifies their intent, and drives them to the right outcome—whether that's a booked appointment, a sales hand-off, or an urgent dispatch.
          </motion.p>

          {/* Animated "live metric" pill */}
          <motion.div
            variants={itemVariants}
            className="mt-4 inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-xl"
          >
            <div className="relative">
              <motion.div
                {...pulseRing}
                className="absolute inset-0 rounded-full bg-blue-400/20 blur-[6px]"
              />
              <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-white shadow-lg shadow-blue-500/50">
                <LineChart className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-0.5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">
                Live Revenue Signals
              </p>
              <p className="text-sm text-slate-600">
                Every answered call becomes a trackable data point. See exactly how much revenue your phone line is generating in real-time.
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Right: Animated feature grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="w-full lg:max-w-xl"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {points.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.label}
                  variants={itemVariants}
                  whileHover={{
                    y: -4,
                    scale: 1.02,
                    transition: { duration: 0.18, ease: "easeOut" },
                  }}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-lg hover:shadow-2xl hover:border-blue-300 transition-all duration-300 hover:-translate-y-1 sm:p-5"
                >
                  {/* Top accent line */}
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500/0 via-blue-500 to-purple-500/0" />

                  {/* Hover glow */}
                  <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-violet-500/5 to-purple-500/5" />
                  </div>

                  <div className="relative flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 border border-blue-500/20 group-hover:bg-blue-500/20 transition-colors">
                      <Icon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-sm font-semibold text-slate-900">
                        {item.label}
                      </h3>
                      <p className="text-xs leading-relaxed text-slate-600">
                        {item.body}
                      </p>
                    </div>
                  </div>

                  {/* Index pill */}
                  <div className="relative mt-3 flex items-center justify-between text-[11px] text-slate-500">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium">
                      Signal #{index + 1}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-blue-600 font-semibold">
                      Always-on
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
